import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Connection } from 'app/types';
import type { UseQueryWrapper } from 'app/types/useQuery.types';
import type { BatchError } from 'app/Domain/Connections/Services/ProcessesService';
import { processesService } from 'app/Domain/Connections/Services/ProcessesService';
import { useSnakeBar } from 'app/hooks/useSnakeBar';

type GetProcessErrorsQueryVariables = {
  connectionId: Connection['connectionId'] | undefined;
  mongoId: string | undefined;
  page?: number;
  perPage?: number;
};

export type GetProcessErrorsQueryResultData = {
  data: BatchError[];
  totalCount: number;
};

const queryFn =
  ({ connectionId, mongoId, page, perPage }: GetProcessErrorsQueryVariables) =>
  async () => {
    if (connectionId && mongoId) {
      const response = await processesService.getBatchErrors({ connectionId, mongoId, page, perPage });
      return response.data;
    }
  };

const QUERY_KEY = 'getProcessErrors';

export const useProcessGetErrorsQuery: UseQueryWrapper<
  GetProcessErrorsQueryResultData,
  GetProcessErrorsQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  const select = React.useCallback((data: any) => {
    return {
      data: (data?.['hydra:member'] ?? data ?? []) as BatchError[],
      totalCount: (data?.['hydra:totalItems'] ?? data?.length ?? 0) as number,
    };
  }, []);

  return useQuery<GetProcessErrorsQueryResultData>(
    [QUERY_KEY, variables.connectionId, variables.mongoId, variables.page, variables.perPage],
    queryFn(variables),
    {
      select,
      onError,
      enabled: !!variables.connectionId && !!variables.mongoId,
      ...(options || {}),
    },
  );
};
